import { defineStore } from 'pinia';
import { apiCall } from '@/utils/globalFunctions';
import store from '@/store/store';

export const useTaxStore = defineStore('taxes', {
  state: () => ({
    userId: store.state.user?.user_id || null,
    taxes: [],
    loading: false,
    error: null,
    currentCompanyId: null,
  }),

  getters: {
    filteredTaxes: (state) => state.taxes,
    defaultTax: (state) => state.taxes.find(tax => tax.is_default) || null,
    activeTaxes: (state) => state.taxes.filter(tax => tax.is_active),
    getTaxById: (state) => (id) => state.taxes.find(tax => tax.tax_id === id),
  },

  actions: {
    setUserId(userId) {
      this.userId = userId;
      this.reset();
    },
    reset() {
      this.$reset();
      this.userId = null;
    },
    setError(err, fallbackMessage, fallbackCode) {
      this.error = {
        message: err?.message || fallbackMessage,
        code: err?.code || fallbackCode,
        timestamp: new Date().toISOString(),
      };
    },

    async fetchTaxes(companyId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return [];
      }
      if (!companyId) {
        this.setError(null, 'Missing company ID in fetchTaxes', 'MISSING_COMPANY_ID');
        return;
      }

      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          url: '/Tax/getTaxes',
          method: 'GET',
          data: { companyId },
        }, store);

        const data = response?.data || [];

        if (Array.isArray(data)) {
          this.taxes = data;
          this.currentCompanyId = companyId;
          return data;
        }

        throw new Error(response.message || 'Invalid response format');
      } catch (err) {
        this.setError(err, 'Failed to fetch taxes', 'FETCH_TAXES_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async addTax(taxData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Tax/addTax',
          data: taxData,
        }, store);

        if (response.status === 'success') {
          await this.fetchTaxes(taxData.company_id);
          return response;
        }

        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to add tax', 'ADD_TAX_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async updateTax(taxData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const response = await apiCall({
          method: 'POST',
          url: '/Tax/updateTax',
          data: taxData,
        }, store);

        if (response.status === 'success') {
          await this.fetchTaxes(taxData.company_id);
          return response;
        }

        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to update tax', 'UPDATE_TAX_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async deleteTax({ taxId, companyId }) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const tax = this.taxes.find(t => t.tax_id === taxId);

        if (tax?.is_default) {
          throw new Error('The default tax cannot be deleted.');
        }

        const response = await apiCall({
          method: 'POST',
          url: '/Tax/deleteTax',
          data: { taxId, companyId },
        }, store);

        if (response.status === 'success') {
          this.taxes = this.taxes.filter(t => t.tax_id !== taxId);
          return response;
        }

        throw new Error(response.message);
      } catch (err) {
        this.setError(err, 'Failed to delete tax', 'DELETE_TAX_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async calculateTax(amount, taxId) {
      const tax = this.getTaxById(taxId);
      if (!tax) throw new Error('Tax rate not found');
      return amount * ((tax.rate || 0) / 100);
    },

    resetError() {
      this.error = null;
    },
  },
});
